import React, { useState } from 'react';
import { Table, Button, Input, InputNumber, Modal, Typography, Row, Col, Card } from 'antd';
import { PlusOutlined, DeleteOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { FJ131, FJ132 } from '../../../services/j203Api';

const { Text } = Typography;

interface Tab2Props {
  rows: Partial<FJ131>[];
  isEditing: boolean;
  totalItems: number;
  onRowsChange: (updater: (prev: Partial<FJ131>[]) => Partial<FJ131>[]) => void;
}

const Tab2Details: React.FC<Tab2Props> = ({ rows, isEditing, totalItems, onRowsChange }) => {
  const [boardModal, setBoardModal] = useState<{ visible: boolean; rowIdx: number }>({ visible: false, rowIdx: -1 });
  const [selectedRowIdx, setSelectedRowIdx] = useState<number>(-1);

  const addRow = () => {
    onRowsChange(prev => [...prev, {
      fj131_02: prev.length + 1,
      fj131_10: '', fj131_11: '', fj131_12: '', fj131_13: '',
      fj131_15: '', fj131_16: 0, fj131_161: 0, fj131_17: 0, fj131_18: 1,
      fj131_19: '', fj131_20: '', fj131_23: 0, fj131_231: 0,
      fj131_191: '', fj131_192: '', fj131_25: '',
      boards: [],
    }]);
  };

  const removeRow = (idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fj131_02: i + 1 })));
    if (selectedRowIdx === idx) setSelectedRowIdx(-1);
    else if (selectedRowIdx > idx) setSelectedRowIdx(selectedRowIdx - 1);
  };

  const updateField = (idx: number, key: keyof FJ131, value: unknown) => {
    onRowsChange(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [key]: value };
      // Auto-calculate amount when price or quantity changes
      if (key === 'fj131_16' || key === 'fj131_17') {
        const price = key === 'fj131_16' ? (value as number) : (r.fj131_16 || 0);
        const qty = key === 'fj131_17' ? (value as number) : (r.fj131_17 || 0);
        updated.fj131_23 = Math.round(price * qty * 100) / 100;
      }
      return updated;
    }));
  };

  // Board modal functions
  const openBoardModal = (idx: number) => setBoardModal({ visible: true, rowIdx: idx });
  const closeBoardModal = () => setBoardModal({ visible: false, rowIdx: -1 });
  const currentBoards = boardModal.rowIdx >= 0 ? (rows[boardModal.rowIdx]?.boards || []) : [];

  const updateBoards = (boards: Partial<FJ132>[]) => {
    const idx = boardModal.rowIdx;
    if (idx < 0) return;
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, boards } as Partial<FJ131> : r));
  };

  const addBoard = () => {
    updateBoards([...currentBoards, { fj132_03: currentBoards.length + 1, fj132_11: '', fj132_12: '' }]);
  };

  const removeBoard = (bIdx: number) => {
    updateBoards(currentBoards.filter((_, i) => i !== bIdx).map((b, i) => ({ ...b, fj132_03: i + 1 })));
  };

  const updateBoardField = (bIdx: number, key: keyof FJ132, value: unknown) => {
    updateBoards(currentBoards.map((b, i) => i === bIdx ? { ...b, [key]: value } : b));
  };

  // Selected row data for side panel
  const selectedRow = selectedRowIdx >= 0 && selectedRowIdx < rows.length ? rows[selectedRowIdx] : null;

  // Grid columns matching FoxPro display order
  const columns = [
    { title: <span data-contract-field="fj131_02">項次</span>, dataIndex: 'fj131_02', width: 50, render: (_: unknown, __: unknown, i: number) => i + 1 },
    {
      title: <span data-contract-field="fj131_10">庫位</span>, dataIndex: 'fj131_10', width: 60,
      render: (v: string, _: unknown, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateField(i, 'fj131_10', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fj131_product">產品代號</span>, width: 180,
      render: (_: unknown, row: Partial<FJ131>, i: number) => isEditing
        ? <div style={{ display: 'flex', gap: 4 }}>
            <Input size="small" value={row.fj131_11 || ''} onChange={e => updateField(i, 'fj131_11', e.target.value)} style={{ flex: 1 }} placeholder="代號1" />
            <Input size="small" value={row.fj131_12 || ''} onChange={e => updateField(i, 'fj131_12', e.target.value)} style={{ width: 60 }} placeholder="代號2" />
          </div>
        : `${row.fj131_11 || ''}${row.fj131_12 ? ' ' + row.fj131_12 : ''}`,
    },
    {
      title: <span data-contract-field="fj131_13">批號</span>, dataIndex: 'fj131_13', width: 140,
      render: (v: string, _: unknown, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateField(i, 'fj131_13', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fj131_17">交運數量</span>, dataIndex: 'fj131_17', width: 90,
      render: (v: number, _: unknown, i: number) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => updateField(i, 'fj131_17', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    {
      title: <span data-contract-field="fj131_15">單位</span>, dataIndex: 'fj131_15', width: 50,
      render: (v: string, _: unknown, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateField(i, 'fj131_15', e.target.value)} style={{ width: 50 }} />
        : v,
    },
    {
      title: <span data-contract-field="fj131_18">換算率</span>, dataIndex: 'fj131_18', width: 80,
      render: (v: number, _: unknown, i: number) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => updateField(i, 'fj131_18', val || 1)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(undefined, { maximumFractionDigits: 4 }),
    },
    {
      title: <span data-contract-field="fj131_161">含稅單價</span>, dataIndex: 'fj131_161', width: 90,
      render: (v: number, _: unknown, i: number) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => updateField(i, 'fj131_161', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }),
    },
    {
      title: <span data-contract-field="fj131_16">未稅單價</span>, dataIndex: 'fj131_16', width: 90,
      render: (v: number, _: unknown, i: number) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => updateField(i, 'fj131_16', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }),
    },
    {
      title: <span data-contract-field="fj131_231">含稅金額</span>, dataIndex: 'fj131_231', width: 100,
      render: (v: number) => (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    {
      title: <span data-contract-field="fj131_23">未稅金額</span>, dataIndex: 'fj131_23', width: 100,
      render: (v: number) => (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    {
      title: <span data-contract-field="fj132_11">板號</span>, width: 60,
      render: (_: unknown, __: unknown, i: number) => (
        <Button size="small" type="link" icon={<UnorderedListOutlined />} onClick={() => openBoardModal(i)}>
          {(rows[i]?.boards?.length || 0)}
        </Button>
      ),
    },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: unknown, i: number) => (
        <Button size="small" danger type="link" icon={<DeleteOutlined />} onClick={() => removeRow(i)} />
      ),
    }] : []),
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {isEditing && (
            <Button size="small" icon={<PlusOutlined />} onClick={addRow}>新增明細</Button>
          )}
        </div>
        <Text type="secondary">總項數: <Text strong>{totalItems}</Text></Text>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        {/* Main grid */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Table
            dataSource={rows}
            columns={columns}
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            scroll={{ x: 1400 }}
            onRow={(_, i) => ({
              onClick: () => setSelectedRowIdx(i ?? -1),
              style: i === selectedRowIdx ? { background: '#e6f7ff' } : undefined,
            })}
          />
        </div>

        {/* Side panel - detail fields per FoxPro layout */}
        <Card size="small" title="明細資訊" style={{ width: 240, flexShrink: 0 }}>
          {selectedRow ? (
            <Row gutter={[8, 4]}>
              <Col span={10}><Text type="secondary">等級</Text></Col>
              <Col span={14}><Text>{selectedRow.fj131_20 || '-'}</Text></Col>
              <Col span={10}><Text type="secondary">色號</Text></Col>
              <Col span={14}><Text>{selectedRow.fj131_19 || '-'}</Text></Col>
              <Col span={10}><Text type="secondary">中文色名</Text></Col>
              <Col span={14}><Text>{selectedRow.fj131_191 || '-'}</Text></Col>
              <Col span={10}><Text type="secondary">英文色名</Text></Col>
              <Col span={14}><Text>{selectedRow.fj131_192 || '-'}</Text></Col>
              <Col span={10}><Text type="secondary">印花配色</Text></Col>
              <Col span={14}><Text>{selectedRow.fj131_25 || '-'}</Text></Col>
            </Row>
          ) : (
            <Text type="secondary">點選明細行查看詳情</Text>
          )}
        </Card>
      </div>

      <Modal title={`板號明細 - 項次 ${boardModal.rowIdx + 1}`} open={boardModal.visible} onCancel={closeBoardModal} footer={null} width={500}>
        {isEditing && (
          <div style={{ marginBottom: 8 }}>
            <Button size="small" icon={<PlusOutlined />} onClick={addBoard}>新增板號</Button>
          </div>
        )}
        <Table dataSource={currentBoards} rowKey={(_, i) => String(i)} size="small" pagination={false}
          columns={[
            { title: <span data-contract-field="fj132_03">序號</span>, width: 60, render: (_: unknown, __: unknown, i: number) => i + 1 },
            {
              title: <span data-contract-field="fj132_11">板號</span>, dataIndex: 'fj132_11', width: 200,
              render: (v: string, _: unknown, i: number) => isEditing
                ? <Input size="small" value={v} onChange={e => updateBoardField(i, 'fj132_11', e.target.value)} />
                : v,
            },
            {
              title: <span data-contract-field="fj132_12">備註</span>, dataIndex: 'fj132_12', width: 200,
              render: (v: string, _: unknown, i: number) => isEditing
                ? <Input size="small" value={v} onChange={e => updateBoardField(i, 'fj132_12', e.target.value)} />
                : v,
            },
            ...(isEditing ? [{
              title: '', width: 40,
              render: (_: unknown, __: unknown, i: number) => (
                <Button size="small" danger type="link" icon={<DeleteOutlined />} onClick={() => removeBoard(i)} />
              ),
            }] : []),
          ]}
        />
      </Modal>
    </div>
  );
};

export default React.memo(Tab2Details);
